/**
 * Billing API service for Stripe integration
 */

import { RealtimeChannel } from '@supabase/supabase-js';
import { BILLING_TRIAL_PERIOD_DAYS } from '../../../utils/constants';
import { ApiResponse as APIResponse } from '../../shared/types';
import {
  BillingCycle,
  BillingRecord,
  Invoice,
  PlanRateLimit,
  SubscriptionPackageDetails,
  UsageStats,
} from '../types/billing';
import { SubscriptionPackage } from '../types/company';
import { triggerBillingEdgeFunction } from './billingFunctionResolver';
import {
  getAnnualDiscountPercentage,
  getPackageTotalPriceForCycle,
} from './billingPricing';
import { resolveStripePriceId } from './stripePriceResolver';
import { supabase } from './supabase';

export interface StripeCheckoutSessionResponse {
  success: boolean;
  sessionId?: string;
  sessionUrl?: string;
  error?: string;
}

/**
 * Create a Stripe checkout session for subscription billing
 */
export async function createCheckoutSession(
  packageId: SubscriptionPackage,
  companyId: number,
  billingCycle: BillingCycle = 'monthly',
): Promise<StripeCheckoutSessionResponse> {
  try {
    const { data, error } = await triggerBillingEdgeFunction(
      'create-checkout-session',
      {
        packageId,
        companyId,
        billingCycle,
        trialPeriodDays: BILLING_TRIAL_PERIOD_DAYS,
      },
    );

    if (error) {
      return { success: false, error };
    }

    return data as StripeCheckoutSessionResponse;
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to create checkout session',
    };
  }
}

export const isBillingSetupComplete = (
  billingRecord?: BillingRecord | null,
): billingRecord is BillingRecord =>
  Boolean(
    billingRecord?.stripe_customer_id && billingRecord?.stripe_subscription_id,
  );

/**
 * Fetch current billing/subscription for a company
 */
export const fetchCompanyBilling = async (
  companyId: number,
): Promise<APIResponse<BillingRecord | null>> => {
  try {
    const { data, error } = await supabase
      .from('billing_records')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') {
      return { success: false, error: error.message, data: undefined };
    }

    return {
      success: true,
      data: data
        ? ({
            ...data,
            billing_cycle: data.billing_cycle || 'monthly',
          } as BillingRecord)
        : null,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to fetch billing',
      data: undefined,
    };
  }
};

export const subscribeToCompanyBilling = (
  companyId: number,
  onBillingChanged: () => void,
): (() => void) => {
  const channelName = `company-billing-${companyId}`;
  const channel: RealtimeChannel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'billing_records',
        filter: `company_id=eq.${companyId}`,
      },
      onBillingChanged,
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
};

/**
 * Fetch all subscription packages from database
 */
export const fetchSubscriptionPackages = async (): Promise<
  APIResponse<SubscriptionPackageDetails[]>
> => {
  try {
    const { data, error } = await supabase
      .from('subscription_packages')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    const packages = (data || []).map((pkg) =>
      resolveStripePriceId({
        ...pkg,
        features: Array.isArray(pkg.features)
          ? pkg.features
          : JSON.parse(pkg.features || '[]'),
        price: Number(pkg.price || 0),
        annual_discount_percentage:
          Number(pkg.annual_discount_percentage) ||
          getAnnualDiscountPercentage({
            id: pkg.id,
            annual_discount_percentage: 0,
          }),
        annual_price: getPackageTotalPriceForCycle(
          {
            id: pkg.id,
            price: Number(pkg.price || 0),
            annual_discount_percentage:
              Number(pkg.annual_discount_percentage) ||
              getAnnualDiscountPercentage({
                id: pkg.id,
                annual_discount_percentage: 0,
              }),
            annual_price: Number(pkg.annual_price || 0),
          },
          'annual',
        ),
      } as SubscriptionPackageDetails),
    ) as SubscriptionPackageDetails[];

    return { success: true, data: packages };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch packages',
      data: [],
    };
  }
};

const normalizeInvoice = (invoice: Record<string, any>): Invoice => ({
  amount_due: Number(invoice.amount_due || 0),
  amount_paid: Number(invoice.amount_paid || 0),
  currency: String(invoice.currency || 'usd'),
  due_date: invoice.due_date || null,
  hosted_invoice_url: invoice.hosted_invoice_url || null,
  id: String(invoice.id),
  invoice_date:
    invoice.invoice_date || invoice.created_at || new Date().toISOString(),
  invoice_number: invoice.invoice_number || null,
  invoice_pdf: invoice.invoice_pdf || null,
  paid_at: invoice.paid_at || null,
  period_end: invoice.period_end || null,
  period_start: invoice.period_start || null,
  status: invoice.status || 'draft',
  stripe_invoice_id: String(invoice.stripe_invoice_id || invoice.id),
});

/**
 * Fetch invoices for a company from the local billing invoices table.
 */
export const fetchCompanyInvoices = async (
  companyId: number,
): Promise<APIResponse<Invoice[]>> => {
  try {
    const { data, error } = await supabase
      .from('billing_invoices')
      .select('*')
      .eq('company_id', companyId)
      .order('invoice_date', { ascending: false });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return { success: true, data: (data || []).map(normalizeInvoice) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch invoices',
      data: [],
    };
  }
};

/**
 * Fetch usage stats for a company (outlets, users)
 */
export const fetchUsageStats = async (
  companyId: number,
  packageType: string,
): Promise<APIResponse<UsageStats>> => {
  try {
    const [{ count: outletCount }, { count: userCount }, { data: rateLimit }] =
      await Promise.all([
        supabase
          .from('outlets')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId),
        supabase
          .from('company_users_view')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId),
        supabase
          .from('plan_rate_limits')
          .select('*')
          .eq('plan_id', packageType)
          .maybeSingle<PlanRateLimit>(),
      ]);

    const totalOutlets = outletCount || 0;
    const outletLimit = rateLimit?.max_outlets ?? null;

    // Per-outlet plans (e.g. professional): scale user limit by actual outlet count
    const userLimit =
      rateLimit?.max_users_per_outlet != null
        ? Math.min(Math.max(totalOutlets, 1), outletLimit ?? totalOutlets) *
          rateLimit.max_users_per_outlet
        : (rateLimit?.max_users ?? null);

    return {
      success: true,
      data: {
        outlets: { used: totalOutlets, limit: outletLimit },
        users: { used: userCount || 0, limit: userLimit },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to fetch usage stats',
      data: undefined,
    };
  }
};

/**
 * Fetch all plan rate limits from the plan_rate_limits table
 */
export const fetchAllPlanRateLimits = async (): Promise<
  APIResponse<Record<string, PlanRateLimit>>
> => {
  try {
    const { data, error } = await supabase.from('plan_rate_limits').select('*');

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const rateLimits = (data as PlanRateLimit[]).reduce<
      Record<string, PlanRateLimit>
    >((acc, row) => ({ ...acc, [row.plan_id]: row }), {});

    return { success: true, data: rateLimits };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch plan rate limits',
      data: undefined,
    };
  }
};

/**
 * Create billing portal session for managing subscription
 */
export const createBillingPortalSession = async (
  returnUrl: string,
): Promise<APIResponse<{ url: string }>> => {
  try {
    // Get current user
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return {
        success: false,
        error: 'You must be authenticated',
        data: undefined,
      };
    }

    // Get user's company from user_profiles_view
    const { data: userProfile, error: profileError } = await supabase
      .from('user_profiles_view')
      .select('company')
      .eq('id', user.id)
      .single();

    if (profileError || !userProfile?.company?.id) {
      return {
        success: false,
        error: 'User company not found',
        data: undefined,
      };
    }

    const companyId = userProfile.company.id;

    // Get billing record for the company
    const { data: billingRecord, error: billingError } = await supabase
      .from('billing_records')
      .select('stripe_customer_id')
      .eq('company_id', companyId)
      .single();

    if (billingError || !billingRecord?.stripe_customer_id) {
      return {
        success: false,
        error: 'No billing record found',
        data: undefined,
      };
    }

    // Call Edge Function with all required data
    const { data, error } = await triggerBillingEdgeFunction(
      'create-billing-portal',
      {
        return_url: returnUrl,
        stripe_customer_id: billingRecord.stripe_customer_id,
      },
    );

    if (error) {
      return { success: false, error, data: undefined };
    }

    return { success: true, data: { url: data.url } };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to create billing portal session',
      data: undefined,
    };
  }
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async (
  cancelAtPeriodEnd: boolean = true,
): Promise<APIResponse<void>> => {
  try {
    const { error } = await triggerBillingEdgeFunction('cancel-subscription', {
      cancel_at_period_end: cancelAtPeriodEnd,
    });
    // const { error } = await supabase.functions.invoke('cancel-subscription', {
    //   body: { cancel_at_period_end: cancelAtPeriodEnd },
    // });

    if (error) {
      return { success: false, error, data: undefined };
    }

    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to cancel subscription',
      data: undefined,
    };
  }
};

/**
 * Resume subscription (if canceled)
 */
export const resumeSubscription = async (): Promise<APIResponse<void>> => {
  try {
    const { error } = await triggerBillingEdgeFunction(
      'resume-subscription',
      {},
    );
    // const { error } = await supabase.functions.invoke(
    //   'resume-subscription',
    //   {},
    // );

    if (error) {
      return { success: false, error, data: undefined };
    }

    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to resume subscription',
      data: undefined,
    };
  }
};

export interface SubscriptionUpdatePreview {
  success: boolean;
  amountDue: number;
  currency: string;
  periodStart: number | null;
  periodEnd: number | null;
  error?: string;
}

/**
 * Preview what a plan switch will charge today, without applying it.
 */
export const previewSubscriptionUpdate = async (
  newPackage: string,
  billingCycle: BillingCycle = 'monthly',
): Promise<SubscriptionUpdatePreview> => {
  try {
    const { data, error } = await triggerBillingEdgeFunction(
      'update-subscription',
      {
        billing_cycle: billingCycle,
        package: newPackage,
        preview: true,
      },
    );

    if (error) {
      return {
        success: false,
        amountDue: 0,
        currency: 'usd',
        periodStart: null,
        periodEnd: null,
        error,
      };
    }

    const preview = data as {
      amount_due: number;
      currency: string;
      period_start: number | null;
      period_end: number | null;
    };

    return {
      success: true,
      amountDue: preview.amount_due,
      currency: preview.currency,
      periodStart: preview.period_start,
      periodEnd: preview.period_end,
    };
  } catch (error) {
    return {
      success: false,
      amountDue: 0,
      currency: 'usd',
      periodStart: null,
      periodEnd: null,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to preview subscription update',
    };
  }
};

/**
 * Update subscription package
 */
export const updateSubscriptionPackage = async (
  newPackage: string,
  billingCycle: BillingCycle = 'monthly',
): Promise<APIResponse<void>> => {
  try {
    const { error } = await triggerBillingEdgeFunction('update-subscription', {
      billing_cycle: billingCycle,
      package: newPackage,
    });
    // const { error } = await supabase.functions.invoke('update-subscription', {
    //   body: { package: newPackage },
    // });

    if (error) {
      return { success: false, error, data: undefined };
    }

    return { success: true, data: undefined };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to update subscription',
      data: undefined,
    };
  }
};

export const updatePendingBillingSelection = async (
  companyId: number,
  packageId: SubscriptionPackage,
  billingCycle: BillingCycle = 'monthly',
): Promise<APIResponse<{ trialEndsAt: string }>> => {
  try {
    const trialEndsAt = new Date();
    trialEndsAt.setDate(trialEndsAt.getDate() + BILLING_TRIAL_PERIOD_DAYS);

    const { error } = await supabase
      .from('billing_records')
      .update({
        billing_cycle: billingCycle,
        package: packageId,
        trial_ends_at: trialEndsAt.toISOString(),
      })
      .eq('company_id', companyId);

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return {
      success: true,
      data: {
        trialEndsAt: trialEndsAt.toISOString(),
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to update billing selection',
      data: undefined,
    };
  }
};
